import { useCallback, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useApolloClient, useLazyQuery, useMutation } from "@apollo/client/react"
import { CalendarItemFullDocument, CalendarItemUpdateWorkoutDocument } from "@/graphql/main/gql/graphql"
import type { LactateMeasurement } from "@/model/NativeBridge/types"
import { useMeContext } from "@/model/user/provider"
import { addLogError } from "@/model/logger"
import { evictDays, refreshAfterLocalChange, refreshFromEvent } from "@/model/calendar/cache/refreshCalendar"

const BUFFER_BEFORE_MS = 5 * 60 * 1000
const BUFFER_AFTER_MS = 30 * 60 * 1000
const TIME_TOLERANCE_SEC = 60

export interface CalendarRecord {
  id: string
  date: string
  data?: {
    lactateMeasurements?: readonly {
      lactateInMMolPerL: number
      lactateInMMolPerLPlan?: number | null
      timeInSec: number
      timeAt?: string | null
      description?: string | null
    }[]
    workoutDetails?: {
      durationInSeconds?: number | null
      startTimeInSeconds?: string | number | null // ISO string or seconds from midnight
    } | null
  } | null
}

export type ImportResult = { outcome: "imported" } | { outcome: "no-match" } | { outcome: "error"; message: string }

export function computeWorkoutStartMs(date: string, startTimeInSeconds?: string | number | null): number {
  if (typeof startTimeInSeconds === "string") {
    const parsed = new Date(startTimeInSeconds).getTime()
    if (!isNaN(parsed)) return parsed
  }
  if (typeof startTimeInSeconds === "number") {
    const dayStart = new Date(`${date}T00:00:00`).getTime()
    return dayStart + startTimeInSeconds * 1000
  }
  return new Date(`${date}T00:00:00`).getTime()
}

export function findMatchingWorkout(
  measurementTimestampMs: number,
  records: readonly CalendarRecord[],
): CalendarRecord | null {
  for (const record of records) {
    if (!record.data) continue
    const details = record.data.workoutDetails
    if (details?.startTimeInSeconds == null) continue // no time info — can't match reliably

    const startMs = computeWorkoutStartMs(record.date, details.startTimeInSeconds)
    const durationMs = (details.durationInSeconds ?? 3600) * 1000
    if (
      measurementTimestampMs >= startMs - BUFFER_BEFORE_MS &&
      measurementTimestampMs <= startMs + durationMs + BUFFER_AFTER_MS
    ) {
      return record
    }
  }
  return null
}

function formatDateISO(timestampMs: number): string {
  const d = new Date(timestampMs)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Shared "attach a lactometer measurement to the matching workout" logic. Used
 * both by the manual per-measurement import button (MeasurementImportView) and
 * the background auto-import runner (useLactateAutoImport) so the matching +
 * GraphQL write is defined once.
 *
 * The write is idempotent per row: it fills an empty planned lactate row within
 * ±60 s of the measurement, else appends a new row. Callers dedupe at the
 * measurement level so a measurement is never re-attached.
 *
 * `isBackground` says which refresh the write earns. The auto-import runner is
 * mounted app-wide and fires on a device event, not on anything the user just
 * did here — so it drops the cached views of THAT DAY and refreshes on the
 * remote throttle, instead of dropping every cached view of the calendar and
 * sending each mounted week back to the network (refreshCalendar).
 */
export function useLactateWorkoutImport({ isBackground = false }: { isBackground?: boolean } = {}) {
  const { t } = useTranslation()
  const { me } = useMeContext()
  // network-only: this read feeds a read-modify-write — the whole lactate array
  // is written back — so it must see the previous import's write. A cached
  // answer would be the array as it was before it (the mutation returns a
  // Boolean and writes nothing to the cache), and the auto-import runs
  // measurement after measurement, so each would overwrite the one before.
  // The invalidation below is for the calendar views, not for this read.
  const [fetchCalendarItems] = useLazyQuery(CalendarItemFullDocument, { fetchPolicy: "network-only" })
  const [updateWorkout] = useMutation(CalendarItemUpdateWorkoutDocument)
  const client = useApolloClient()
  /** Days a background sweep has written, waiting for its single collapse. */
  const pendingDaysRef = useRef<Set<string>>(new Set())

  const importMeasurement = useCallback(
    async (measurement: LactateMeasurement): Promise<ImportResult> => {
      try {
        const day = formatDateISO(measurement.timestamp)
        const { data } = await fetchCalendarItems({
          variables: { request: { dates: [day] } },
        })

        const records = (data?.calendarItems?.records ?? []) as readonly CalendarRecord[] // eslint-disable-line @typescript-eslint/no-unnecessary-condition -- data can be undefined at runtime
        const match = findMatchingWorkout(measurement.timestamp, records)
        if (!match) return { outcome: "no-match" }

        const workoutStartMs = computeWorkoutStartMs(match.date, match.data?.workoutDetails?.startTimeInSeconds)
        const timeInSec = Math.max(0, Math.round((measurement.timestamp - workoutStartMs) / 1000))
        const lactateValue = measurement.value / 10.0

        const existingLactate = (match.data?.lactateMeasurements ?? []).map((lm) => ({
          lactateInMMolPerL: lm.lactateInMMolPerL,
          lactateInMMolPerLPlan: lm.lactateInMMolPerLPlan ?? undefined,
          timeInSec: lm.timeInSec,
          timeAt: lm.timeAt ?? undefined,
          description: lm.description ?? undefined,
        }))

        // Fill an existing empty planned row within tolerance, else append.
        const matchingIdx = existingLactate.findIndex(
          (lm) => Math.abs(lm.timeInSec - timeInSec) <= TIME_TOLERANCE_SEC && !lm.lactateInMMolPerL,
        )

        let allLactate
        const matching = existingLactate[matchingIdx]
        if (matching) {
          allLactate = [...existingLactate]
          allLactate[matchingIdx] = {
            ...matching,
            lactateInMMolPerL: lactateValue,
            timeAt: new Date(measurement.timestamp).toISOString(),
          }
        } else {
          allLactate = [
            ...existingLactate,
            {
              lactateInMMolPerL: lactateValue,
              timeInSec,
              timeAt: new Date(measurement.timestamp).toISOString(),
              description: t("lactometer.importedFromDevice"),
            },
          ]
        }
        allLactate.sort((a, b) => a.timeInSec - b.timeInSec)

        await updateWorkout({
          variables: {
            id: match.id,
            activity: { date: day, userID: me.user.id },
            data: { lactateMeasurements: allLactate },
          },
        })

        // The mutation returns a Boolean, so Apollo can't update anything on
        // its own. The server's calendar.changed event re-reads the day like
        // for an edit made on another device; this local re-read covers the
        // paths without a channel, so the lactate values show at once.
        if (isBackground) {
          // Collected, not applied: a sweep attaches measurement after
          // measurement, and each collapse walks every ROOT_QUERY field and
          // runs a cache GC. One pass, one collapse — see flushBackgroundRefresh.
          pendingDaysRef.current.add(day)
        } else {
          void refreshAfterLocalChange(client.cache, {
            calendar: [{ userId: me.user.id, from: day, to: day }],
            health: [],
          })
        }

        return { outcome: "imported" }
      } catch (error) {
        addLogError("useLactateWorkoutImport: import failed", error)
        const message = error instanceof Error ? error.message : String(error)
        return { outcome: "error", message }
      }
    },
    [fetchCalendarItems, updateWorkout, client, t, me.user.id, isBackground],
  )

  /**
   * Ends a background sweep: drops the cached views of every day it wrote and
   * refreshes what is on screen, once. The manual path needs none of this — it
   * refreshes per import, in settings, where nothing of the calendar is mounted.
   */
  const flushBackgroundRefresh = useCallback(() => {
    const days = [...pendingDaysRef.current]
    if (days.length === 0) return
    pendingDaysRef.current.clear()
    evictDays(client.cache, me.user.id, days)
    void refreshFromEvent({
      calendar: days.map((day) => ({ userId: me.user.id, from: day, to: day })),
      health: [],
    })
  }, [client, me.user.id])

  return { importMeasurement, flushBackgroundRefresh }
}
